"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, useCallback } from "react";

import Button from "@ingka/button";
import ChevronRight from "@ingka/ssr-icon/paths/chevron-right-small";
import ChevronLeft from "@ingka/ssr-icon/paths/chevron-left-small";
import cx from "classnames";
import bezier from "bezier-easing";
import debounce from "@ingka/carousel-shared/utils/debounce";
import smoothScroll from "@ingka/carousel-shared/utils/smoothScroll";
import {
  scrollLeftDirection,
  scrollLeftNextCustom,
  scrollLeftPrevCustom
} from "@ingka/carousel-shared/utils/rtlScrollLeft";
const _getVisibleMinMax = (indexes, minmax) => {
  if (indexes === void 0 || indexes.length === 0) {
    return null;
  }
  return Math[minmax].apply(Math, indexes);
};
const OverflowCarousel = (props) => {
  const {
    children,
    prefix = "",
    id = "default",
    className,
    hasNoControls = false,
    controlBtnType = "primary",
    ariaLabelRightBtn = "See next items",
    ariaLabelLeftBtn = "See previous items",
    onBtnRightClick,
    onBtnLeftClick,
    rtl,
    slideIndex = null,
    ...otherProps
  } = props;
  const scrollingContainer = useRef(null);
  const slideStateCache = React.useRef([]);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const [rtlScrollType, setRtlScrollType] = useState(null);
  const [swipeOnly, setSwipeOnly] = useState(false);
  const [firstTouchEvent, setFirstTouchEvent] = useState(false);
  const [intersectionObserverDisabled, setIntersectionObserverDisabled] = useState(false);
  const [slideStates, setSlideStates] = useState({
    visibleIndexes: [],
    visibleSlides: [],
    partiallyVisibleIndexes: [],
    partiallyVisibleSlides: []
  });
  const [slidesArray, setSlidesArray] = useState([]);
  const previousBtnId = `${prefix}overflow-carousel__${id}-prev-btn`;
  const nextBtnId = `${prefix}overflow-carousel__${id}-next-btn`;
  let _visibleSlides = [];
  let _partiallyVisibleSlides = [];
  const nextTarget = () => {
    if (slideStates.visibleIndexes.length === 0 && slideStates.partiallyVisibleIndexes) {
      const nextIndex = _getVisibleMinMax(slideStates.partiallyVisibleIndexes, "max") + 1;
      if (nextIndex > slidesArray.length - 1) {
        return slidesArray[nextIndex - 1];
      }
      return slidesArray[nextIndex];
    }
    if (slidesArray[_getVisibleMinMax(slideStates.visibleIndexes, "max")] === null) {
      return slidesArray[_getVisibleMinMax(slideStates.partiallyVisibleIndexes, "max")];
    }
    return slidesArray[_getVisibleMinMax(slideStates.visibleIndexes, "max") + 1];
  };
  const prevTarget = () => {
    if (slideStates.visibleIndexes.length === 0 && slideStates.partiallyVisibleIndexes) {
      const prevIndex = _getVisibleMinMax(slideStates.partiallyVisibleIndexes, "min") - 1;
      if (prevIndex < 0) {
        return slidesArray[0];
      }
      return slidesArray[prevIndex];
    }
    if (slidesArray[_getVisibleMinMax(slideStates.visibleIndexes, "min")] === null) {
      return slidesArray[_getVisibleMinMax(slideStates.partiallyVisibleIndexes, "min")];
    }
    return slidesArray[_getVisibleMinMax(slideStates.visibleIndexes, "min") - 1];
  };
  const isFirstSlideVisible = () => {
    if (rtl ? rtlScrollType === "reversed" && scrollingContainer.current.scrollLeft === maxScrollWidth() : scrollingContainer.current.scrollLeft === 0) {
      return true;
    }
    if (slideStates.visibleSlides.indexOf(slidesArray[0]) > -1) {
      return true;
    }
    return false;
  };
  const isLastSlideVisible = () => {
    if (slideStates.visibleSlides.indexOf(lastSlide) > -1) {
      return true;
    }
    if (rtl ? rtlScrollType === "reversed" && scrollingContainer.current.scrollLeft === 0 : scrollingContainer.current.scrollLeft === maxScrollWidth()) {
      return true;
    }
    return false;
  };
  const lastSlide = slidesArray[slidesArray.length - 1];
  const maxScrollWidth = () => {
    if (!scrollingContainer.current) {
      return Infinity;
    }
    return scrollingContainer.current.scrollWidth - scrollingContainer.current.offsetWidth;
  };
  const getPreviousScroll = (target) => {
    return scrollLeftPrevCustom(
      rtl,
      rtlScrollType,
      target,
      scrollingContainer.current
    );
  };
  const getNextScroll = (target) => {
    return scrollLeftNextCustom(
      rtl,
      rtlScrollType,
      target,
      scrollingContainer.current
    );
  };
  const easing = (elapsed, offset, gap, duration) => {
    const f = bezier(0.4, 0, 0.4, 1);
    const progress = elapsed / duration;
    return gap * f(progress) + offset;
  };
  const observeSlides = useCallback((entries, observer) => {
    entries.forEach((entry) => {
      const validObserver = observer && observer.thresholds;
      if (validObserver && entry.intersectionRatio >= observer.thresholds[1]) {
        if (_visibleSlides.indexOf(entry.target) === -1) {
          _visibleSlides.push(entry.target);
        }
      } else {
        _visibleSlides = _visibleSlides.filter((item) => item !== entry.target);
      }
      if (validObserver && entry.intersectionRatio >= observer.thresholds[0]) {
        if (_partiallyVisibleSlides.indexOf(entry.target) === -1) {
          _partiallyVisibleSlides.push(entry.target);
        }
      } else {
        _partiallyVisibleSlides = _partiallyVisibleSlides.filter(
          (item) => item !== entry.target
        );
      }
    });
    setSlideStates({
      visibleIndexes: _visibleSlides.map(
        (visible) => slidesArray.indexOf(visible)
      ),
      visibleSlides: _visibleSlides,
      partiallyVisibleIndexes: _partiallyVisibleSlides.map(
        (visible) => slidesArray.indexOf(visible)
      ),
      partiallyVisibleSlides: _partiallyVisibleSlides
    });
  });
  const scrollToTarget = useCallback((nextSlide) => {
    setIsScrolling(true);
    smoothScroll(
      scrollingContainer.current,
      "scrollLeft",
      nextSlide,
      easing,
      200,
      () => {
        setIsScrolling(false);
      }
    );
  }, []);
  const onButtonClick = useCallback((direction) => {
    if (isScrolling)
      return;
    const nextElement = nextTarget();
    const previousElement = prevTarget();
    let nextSlide = null;
    if (direction === "right" && nextElement) {
      nextSlide = getNextScroll(nextElement);
    } else if (direction !== "right" && previousElement) {
      nextSlide = getPreviousScroll(previousElement);
    }
    if (nextSlide) {
      scrollToTarget(nextSlide);
    }
    if (direction === "right" && onBtnRightClick) {
      onBtnRightClick();
    }
    if (direction === "left" && onBtnLeftClick) {
      onBtnLeftClick();
    }
  });
  const onFirstTap = useCallback((e) => {
    if (firstTouchEvent)
      return;
    if (e.target.parentNode.classList.contains(
      `${prefix}overflow-carousel__button`
    )) {
      setFirstTouchEvent(true);
      setSwipeOnly(false);
    }
  });
  const onFirstSwipe = useCallback((e) => {
    if (firstTouchEvent)
      return;
    setFirstTouchEvent(true);
    if (!e.target.parentNode.classList.contains(
      `${prefix}overflow-carousel__button`
    )) {
      setSwipeOnly(true);
    }
  });
  const resizeEventCallback = useCallback(
    debounce(() => {
      if (slideStates.partiallyVisibleSlides === void 0 || slideStates.partiallyVisibleSlides.length === 0)
        return;
      setIsFirstSlide(isFirstSlideVisible);
      setIsLastSlide(isLastSlideVisible);
    }, 250)
  );
  useEffect(() => {
    const parentNode = scrollingContainer.current.parentNode;
    parentNode.addEventListener("touchstart", onFirstTap, {
      once: true,
      passive: true
    });
    parentNode.addEventListener("touchmove", onFirstSwipe, {
      once: true,
      passive: true
    });
    return () => {
      parentNode.removeEventListener("touchstart", onFirstTap);
      parentNode.removeEventListener("touchmove", onFirstSwipe);
    };
  }, [firstTouchEvent, onFirstSwipe]);
  useEffect(() => {
    if (slideStates.partiallyVisibleSlides === void 0 || slideStates.partiallyVisibleSlides.length === 0)
      return;
    setIsFirstSlide(isFirstSlideVisible);
    setIsLastSlide(isLastSlideVisible);
  }, [slideStates, rtl]);
  const setCacheVisibleSlides = useCallback((e) => {
    setSlideStates((state) => {
      slideStateCache.current = state;
      return state;
    });
  }, []);
  const focusEventCallback = useCallback((e) => {
    if (slideStateCache.current.visibleSlides && slideStateCache.current.visibleSlides.indexOf(e.target) !== -1)
      return;
    if (!["Tab", "ArrowRight", "ArrowLeft"].includes(e.key))
      return;
    const isForward = !e.shiftKey && e.key === "Tab" || // If tab key was pressed without shift
    e.key === "ArrowRight" && !rtl || // or LTR and and right arrow
    e.key === "ArrowLeft" && rtl;
    const nextSlide = isForward ? getNextScroll(e.target) : getPreviousScroll(e.target);
    scrollToTarget(nextSlide * (rtl ? -1 : 1));
  }, []);
  useEffect(() => {
    const arr = [].slice.call(scrollingContainer.current.children);
    arr.forEach(
      (slide) => !!slide && slide.addEventListener("keyup", focusEventCallback)
    );
    arr.forEach(
      (slide) => !!slide && slide.addEventListener("keydown", setCacheVisibleSlides)
    );
    setSlidesArray(arr);
    window.addEventListener("resize", resizeEventCallback);
    return () => {
      window.removeEventListener("resize", resizeEventCallback);
      arr.forEach(
        (slide) => !!slide && slide.removeEventListener("keyup", focusEventCallback)
      );
      arr.forEach(
        (slide) => !!slide && slide.removeEventListener("keydown", setCacheVisibleSlides)
      );
    };
  }, [React.Children.toArray(children).length]);
  useEffect(() => {
    if ("IntersectionObserver" in window && "IntersectionObserverEntry" in window) {
      const visibleSlideObserver = new IntersectionObserver(observeSlides, {
        root: scrollingContainer.current,
        threshold: [0.99, 1e-3],
        rootMargin: "1px"
      });
      slidesArray.forEach(
        (slide) => !!slide && visibleSlideObserver.observe(slide)
      );
      setIntersectionObserverDisabled(false);
      return () => {
        visibleSlideObserver.disconnect();
      };
    } else {
      setIntersectionObserverDisabled(true);
    }
  }, [slidesArray]);
  useEffect(() => {
    setRtlScrollType(scrollLeftDirection());
  }, [rtl]);
  useEffect(() => {
    if (Number.isNaN(Number(slideIndex)) || slidesArray.length === 0 || slideIndex === null)
      return;
    const clampedSlideIndex = Math.max(
      Math.min(slideIndex, slidesArray.length - 1),
      0
    );
    const nextSlide = getNextScroll(slidesArray[clampedSlideIndex]);
    scrollToTarget(nextSlide);
  }, [slidesArray, slideIndex, rtl]);
  const baseClass = `${prefix}overflow-carousel`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cx(
        baseClass,
        intersectionObserverDisabled && `${baseClass}--intersection-observer-disabled`,
        className
      ),
      ...otherProps,
      children: [
        !hasNoControls && !swipeOnly && /* @__PURE__ */ jsx(
          Button,
          {
            id: previousBtnId,
            "aria-controls": `${baseClass}__${id}-prev-btn`,
            prefix,
            size: "xsmall",
            iconOnly: true,
            inverseTheme: true,
            staticColour: true,
            ssrIcon: ChevronLeft,
            type: controlBtnType,
            "aria-label": ariaLabelLeftBtn,
            className: cx(
              `${baseClass}__button`,
              isFirstSlide && `${baseClass}__button--hidden`
            ),
            onClick: () => onButtonClick("left")
          }
        ),
        /* @__PURE__ */ jsx("div", { className: `${baseClass}__content`, ref: scrollingContainer, children }),
        !hasNoControls && !swipeOnly && /* @__PURE__ */ jsx(
          Button,
          {
            id: nextBtnId,
            "aria-controls": `${baseClass}__${id}-next-btn`,
            prefix,
            size: "xsmall",
            iconOnly: true,
            inverseTheme: true,
            staticColour: true,
            ssrIcon: ChevronRight,
            type: controlBtnType,
            "aria-label": ariaLabelRightBtn,
            className: cx(
              `${baseClass}__button`,
              `${baseClass}__button--right`,
              isLastSlide && `${baseClass}__button--hidden`
            ),
            onClick: () => onButtonClick("right")
          }
        )
      ]
    }
  );
};
export {
  OverflowCarousel
};
